import React from 'react';

const nodeTypes = [
  {
    type: 'indicator',
    label: '📊 Indicator',
    description: 'SMA, EMA, RSI, MACD, etc.',
    color: '#6366f1',
    defaults: { indicator: 'sma', params: { period: 20 } },
  },
  {
    type: 'price',
    label: '💲 Price',
    description: 'Close, Open, High, Low',
    color: '#8b5cf6',
    defaults: { field: 'close' },
  },
  {
    type: 'value',
    label: '🔢 Value',
    description: 'Static numeric value',
    color: '#ec4899',
    defaults: { value: 0 },
  },
  {
    type: 'data',
    label: '🌐 External Data',
    description: 'VIX, Treasury yields, macro data',
    color: '#0ea5e9',
    defaults: { source: 'vix' },
  },
  {
    type: 'correlation',
    label: '📈 Correlation',
    description: 'Rolling correlation signal',
    color: '#a855f7',
    defaults: { compare_type: 'ticker', compare_value: 'SPY', window: 30 },
  },
  {
    type: 'time',
    label: '🕐 Time Filter',
    description: 'Day of week, time of day, market session',
    color: '#64748b',
    defaults: { filter_type: 'day_of_week', value: 'monday' },
  },
  {
    type: 'lookback',
    label: '⏮ Lookback',
    description: 'Value N bars ago, highest/lowest in N bars',
    color: '#06b6d4',
    defaults: {
      lookback_type: 'bars_ago',
      period: 1,
      field: 'close',
    },
  },
  {
    type: 'timeframe',
    label: '📅 Timeframe',
    description: 'Pull indicator from different timeframe',
    color: '#7c3aed',
    defaults: {
      timeframe: '1day',
      indicator: 'sma',
      params: { period: 200 },
    },
  },
  {
    type: 'fibonacci',
    label: '🌀 Fibonacci',
    description: 'Fib retracement and extension levels',
    color: '#d97706',
    defaults: {
      level: 61.8,
      swing_lookback: 100,
      level_type: 'retracement',
    },
  },
  {
    type: 'trendline',
    label: '📐 Trendline',
    description: 'Dynamic support and resistance',
    color: '#dc2626',
    defaults: {
      type: 'resistance',
      min_touches: 3,
      lookback: 100,
    },
  },
  {
    type: 'pattern',
    label: '🔷 Chart Pattern',
    description: 'Head & shoulders, double top, etc.',
    color: '#7c3aed',
    defaults: {
      pattern: 'head_and_shoulders',
      confirmation: 'neckline_break',
    },
  },
  {
    type: 'volatility',
    label: '⚡ Volatility',
    description: 'ATR, Bollinger Band width, historical vol',
    color: '#b45309',
    defaults: {
      measure: 'atr',
      period: 14,
    },
  },
  {
    type: 'position_age',
    label: '⏱ Position Age',
    description: 'Bars or trading days since entry',
    color: '#14b8a6',
    defaults: {
      unit: 'trading_days',
    },
  },
  {
    type: 'condition',
    label: '⚖️ Condition',
    description: 'Compare two inputs',
    color: '#f59e0b',
    defaults: { operator: 'crosses_above' },
  },
  {
    type: 'logic',
    label: 'Logic Gate',
    description: 'AND / OR with 2+ inputs',
    color: '#f97316',
    defaults: { operator: 'and' },
  },
  {
    type: 'action',
    label: '🎯 Action',
    description: 'Buy, Sell, Short, Cover, or Close',
    color: '#10b981',
    defaults: { action: 'buy', size_pct: 100 },
  },
  {
    type: 'stop_loss',
    label: '🛑 Stop Loss',
    description: 'Percent or ATR-based stop, optionally trailing',
    color: '#f85149',
    defaults: { mode: 'percent', value: 2, atr_period: 14, trailing: false },
  },
  {
    type: 'take_profit',
    label: '🎯 Take Profit',
    description: 'Percent, ATR, or risk:reward target',
    color: '#26a641',
    defaults: { mode: 'percent', value: 4, atr_period: 14 },
  },
  {
    type: 'gap',
    label: '↕ Overnight Gap',
    description: "Today's open vs. prior close, as a %",
    color: '#0891b2',
    defaults: {},
  },
  {
    type: 'streak',
    label: '📉 Streak / Drawdown',
    description: 'Win/loss streak or drawdown from equity peak',
    color: '#be123c',
    defaults: { measure: 'drawdown_pct' },
  },
  {
    type: 'divergence',
    label: '🔀 Divergence',
    description: 'Price vs. indicator divergence (wire an indicator in)',
    color: '#9333ea',
    defaults: { direction: 'bearish', lookback: 14 },
  },
  {
    type: 'pivot',
    label: '🎯 Pivot Point',
    description: 'Classic floor pivot levels (PP, R1-3, S1-3)',
    color: '#ca8a04',
    defaults: { level: 'pp', timeframe: 'daily' },
  },
  {
    type: 'relative_strength',
    label: '💪 Relative Strength',
    description: 'Price ratio or relative momentum vs. another ticker',
    color: '#059669',
    defaults: { compare_type: 'ticker', compare_value: 'SPY', mode: 'ratio', period: 20 },
  },
  {
    type: 'regime',
    label: '🌐 Macro Regime',
    description: 'Trade only during a specific macro regime (growth, volatility, liquidity)',
    color: '#4338ca',
    defaults: { family: 'volatility_risk', regime: 'Calm Risk-On' },
  },
  {
    type: 'earnings',
    label: '📅 Earnings Proximity',
    description: 'Days until/since earnings, or within N days (stocks only)',
    color: '#b45309',
    defaults: { mode: 'days_until', window_days: 5 },
  },
  {
    type: 'swing',
    label: '〽️ Swing Point',
    description: 'Last fractal swing high/low as a price level',
    color: '#0d9488',
    defaults: { which: 'high', left: 2, right: 2, lookback: 60 },
  },
  {
    type: 'structure',
    label: '🏗 Market Structure',
    description: 'Break of structure (BOS) / change of character (CHoCH)',
    color: '#2563eb',
    defaults: { event: 'bos', direction: 'bullish', left: 2, right: 2, lookback: 60 },
  },
  {
    type: 'liquidity_sweep',
    label: '💧 Liquidity Sweep',
    description: 'Stop-hunt: wick pierces a prior swing then closes back',
    color: '#db2777',
    defaults: { direction: 'bearish', left: 2, right: 2, lookback: 60 },
  },
  {
    type: 'order_block',
    label: '🧱 Order Block',
    description: 'Supply/demand zone — in-zone signal or zone edge price',
    color: '#7c3aed',
    defaults: { direction: 'bullish', output: 'in_zone', left: 2, right: 2, lookback: 60 },
  },
  {
    type: 'fvg',
    label: '🕳 Fair Value Gap',
    description: '3-bar imbalance (FVG) — fresh or price-in-zone',
    color: '#0891b2',
    defaults: { direction: 'bullish', output: 'present', lookback: 30 },
  },
  {
    type: 'ote',
    label: '🎯 Optimal Trade Entry',
    description: '0.62–0.79 retracement zone of the last swing leg',
    color: '#d97706',
    defaults: { direction: 'bullish', low_fib: 0.62, high_fib: 0.79, left: 2, right: 2, lookback: 60 },
  },
  {
    type: 'session_range',
    label: '🗓 Session Range',
    description: 'Frozen London/NY/Asia or opening-range high/low (intraday)',
    color: '#64748b',
    defaults: { session: 'london', which: 'high' },
  },
  {
    type: 'round_level',
    label: '🔢 Round Level',
    description: 'Round-number / psychological levels ($50/$100) & breakouts',
    color: '#ca8a04',
    defaults: { step: 100, mode: 'above' },
  },
  {
    type: 'volume_profile',
    label: '📊 Volume Profile',
    description: 'Point of control / value-area high–low over a rolling window',
    color: '#7c3aed',
    defaults: { output: 'poc', lookback: 50, bins: 24, value_area_pct: 70 },
  },
];

export default function Sidebar({ onDragStart, onAddNode }) {
  return (
    <div className="builder-sidebar" style={{
      width: 240,
      background: 'var(--surface)',
      color: 'var(--text)',
      padding: 16,
      overflowY: 'auto',
      flexShrink: 0,
      borderRight: '1px solid var(--border)',
    }}>
      <h3 style={{ margin: '0 0 4px', fontSize: 16 }}>Strategy Blocks</h3>
      <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: 0, marginBottom: 16 }}>
        Drag blocks onto the canvas, then connect them with edges.
      </p>
      <p className="sidebar-mobile-hint" style={{ display: 'none', fontSize: 11, color: 'var(--muted)', margin: '0 0 8px' }}>
        Tap a block to add it to the canvas.
      </p>
      <div className="sidebar-blocks" data-tour="sidebar-blocks">
      {nodeTypes.map((nt) => (
        <div
          key={nt.type}
          draggable
          onDragStart={(e) => {
            e.dataTransfer.setData('application/reactflow-type', nt.type);
            e.dataTransfer.setData('application/reactflow-defaults', JSON.stringify(nt.defaults));
            e.dataTransfer.effectAllowed = 'move';
            if (onDragStart) onDragStart(nt);
          }}
          onClick={() => {
            if (onAddNode) onAddNode(nt.type, nt.defaults);
          }}
          style={{
            padding: 10,
            marginBottom: 10,
            borderRadius: 8,
            border: `2px solid ${nt.color}`,
            background: 'rgba(255,255,255,0.05)',
            cursor: 'grab',
            userSelect: 'none',
          }}
        >
          <div style={{ fontWeight: 600, fontSize: 14 }}>{nt.label}</div>
          <div style={{ fontSize: 11, color: 'var(--muted)' }}>{nt.description}</div>
        </div>
      ))}
      </div>

      <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '16px 0' }} />
      <h4 style={{ margin: '0 0 8px', fontSize: 13 }}>How to Build</h4>
      <ol style={{ paddingLeft: 18, fontSize: 11, lineHeight: 1.8, color: 'var(--muted)' }}>
        <li>Drag <strong>Indicator</strong> or <strong>Price</strong> blocks</li>
        <li>Add a <strong>Condition</strong> block</li>
        <li>Connect left & right inputs</li>
        <li>Add an <strong>Action</strong> block</li>
        <li>Connect condition → action</li>
        <li>Save & run a backtest!</li>
      </ol>
    </div>
  );
}
